import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { X, Trophy } from "lucide-react";
import api from "../lib/api";
import { formatPrice, perSqft, typeLabel } from "../lib/format";
import { useApp } from "../context/AppContext";
import Seo from "../components/Seo";

const rows = [
  ["Price", (p) => formatPrice(p.price)],
  ["Area", (p) => (p.area_sqft ? `${p.area_sqft} sq.ft` : "—")],
  ["₹/sq.ft", (p) => perSqft(p.price, p.area_sqft) || "—"],
  ["Type", (p) => typeLabel(p.property_type)],
  ["Bedrooms", (p) => p.bedrooms ?? "—"],
  ["Bathrooms", (p) => p.bathrooms ?? "—"],
  ["Parking", (p) => p.parking ?? "—"],
  ["Floor", (p) => p.floor || "—"],
  ["Age", (p) => p.property_age || "—"],
  ["Location", (p) => p.sector],
  ["Property Score", (p) => (p.score_overall ? `${p.score_overall}/10` : "—")],
  ["Price Assessment", (p) => p.price_assessment || "—"],
  ["Investment Score", (p) => (p.score_investment ? `${p.score_investment}/10` : "—")],
];

export default function Compare() {
  const { compare, setCompare } = useApp();
  const { data } = useQuery({
    queryKey: ["compare", compare],
    queryFn: async () => (await api.post("/properties/by-ids", { ids: compare })).data,
    enabled: compare.length > 0,
  });
  const items = data?.items || [];
  const best = items.reduce((a, b) => ((b.score_overall || 0) > (a?.score_overall || 0) ? b : a), null);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo title="Compare Properties | Faridabad Property Vault" description="Compare up to 3 Faridabad properties side by side." />
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">Compare Properties</h1>
      <p className="text-slate-500 mt-1">Compare up to 3 properties side by side.</p>

      {items.length === 0 ? (
        <div className="text-center py-24 bg-slate-50 rounded-2xl border border-dashed border-slate-200 mt-8">
          <p className="text-slate-600 font-semibold">No properties added to compare yet.</p>
          <Link to="/properties" className="text-indigo-800 font-semibold text-sm mt-2 inline-block">Browse properties →</Link>
        </div>
      ) : (
        <>
          {best && (
            <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-2xl p-4 flex items-center gap-3" data-testid="best-value">
              <Trophy className="w-6 h-6 text-indigo-700" />
              <div><span className="text-sm text-indigo-800">Best Value (by Property Score): </span><Link to={`/properties/${best.slug}`} className="font-heading font-bold text-indigo-900">{best.title}</Link></div>
            </div>
          )}
          <div className="overflow-x-auto mt-6 rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="compare-table">
              <thead>
                <tr>
                  <th className="p-4 text-left w-40 bg-slate-50"></th>
                  {items.map((p) => (
                    <th key={p.id} className="p-4 text-left min-w-[220px] align-top">
                      <div className="relative">
                        <button onClick={() => setCompare(compare.filter((x) => x !== p.id))} className="absolute -top-2 -right-2 text-slate-400 hover:text-rose-500" data-testid={`compare-remove-${p.property_id}`}><X className="w-4 h-4" /></button>
                        <Link to={`/properties/${p.slug}`}><img src={(p.images?.[0] || "").startsWith("http") ? p.images[0] : ""} alt="" className="w-full h-28 object-cover rounded-lg mb-2 bg-slate-100" onError={(e) => (e.target.style.display = "none")} /></Link>
                        <Link to={`/properties/${p.slug}`} className="font-heading font-bold text-slate-900 hover:text-indigo-800 line-clamp-2">{p.title}</Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, fn], ri) => (
                  <tr key={label} className={ri % 2 ? "bg-slate-50/50" : ""}>
                    <td className="p-4 font-medium text-slate-500">{label}</td>
                    {items.map((p) => <td key={p.id} className="p-4 text-slate-800 font-medium">{fn(p)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
